import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Warning as WarningIcon,
  LocalPharmacy as LocalPharmacyIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import RxDispenseDialog from './RxDispenseDialog';
import EditMedicationDialog from './EditMedicationDialog';
import FillHistoryDialog from './FillHistoryDialog';
import { Medication, medicationService } from '../../services/medicationService';
import {
  datePickerToCalendarValue,
  formatCalendarDate,
  isCalendarDayBeforeToday,
} from '../../utils/dateUtils';
import { useClinicTimezone } from '../../hooks/useClinicTimezone';
import { executeUpdate, OfflineWriteBlockedError } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';
import { patientChartControls } from '../../auth/pageControls';
import { buildLabelData } from './RxLabel';
import { printRxLabels } from './printLabel';

/**
 * The `date` prop as an ISO string `formatCalendarDate` can read.
 *
 * Every live caller passes a STRING already — the chart's `yyyy-MM-dd` grouping
 * key (`HorseProfileClassic` `group.date` / `item.date`) or a row's
 * `Medication.startDate` ISO (`item.startDate`, `EventDetailDrawer`). The `Date`
 * arm only exists because the prop type has always allowed one; a `Date` that
 * means a DAY is read by its LOCAL y/m/d (the day its constructor was given),
 * never by `toISOString()`, which would roll it back in every negative-offset
 * zone — the same rule `datePickerToCalendarValue` applies on the write side.
 */
const calendarProp = (d: string | Date): string | undefined =>
  typeof d === 'string' ? d : datePickerToCalendarValue(d);

interface MedicationCardProps {
  medications: any[]; // Array of medications from Medication table
  /**
   * Optional group date for the card header — a CALENDAR DAY, not a moment.
   * Rendered by its date PART (DATE-KINDS.md §2.2); never re-zoned.
   */
  date?: string | Date;
  title?: string;
  lowStockItems?: string[];
  /**
   * Total non-archived medications across all date groups for this horse.
   * Used to disable the Delete button when only one medication remains, so
   * the horse never has zero medication history. Optional — when omitted
   * (e.g. on non-horse-profile usages) the delete button is hidden entirely.
   */
  totalActiveMedications?: number;
  /** Optional horseId so the delete-confirmation message can reference it. */
  horseId?: string;
}

const MedicationCard: React.FC<MedicationCardProps> = ({
  medications = [],
  date,
  title = 'Medication Record',
  lowStockItems = [],
  totalActiveMedications,
  horseId,
}) => {
  const rbac = useRBAC();
  /**
   * The card's own write controls. It is rendered from both chart layouts on plain data props,
   * so it asks the map directly rather than taking four booleans through two callers.
   *
   * PUT /medications/:id -> manage:medical_records (:69); POST /medications/:id/dispense ->
   * manage:medical_records (:110); DELETE -> manage:medical_records AND
   * manage:medical_records_archive (:101). Reprinting a label is a local print, not a write.
   */
  const can = patientChartControls(rbac);
  const queryClient = useQueryClient();
  // Hoisted: the per-row expiry check below runs inside a .map(), where a hook
  // cannot be called.
  const clinicZone = useClinicTimezone();
  const { enqueueSnackbar } = useSnackbar();
  const [dispenseTarget, setDispenseTarget] = useState<Medication | null>(null);
  const [editTarget, setEditTarget] = useState<Medication | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Medication | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Id of the Rx whose label is currently being (re)printed — drives the
  // per-row spinner on the reprint button.
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  // Rx whose fill history is open (via clicking the "Fills X/Y" chip).
  const [fillHistoryTarget, setFillHistoryTarget] = useState<Medication | null>(null);

  // Show the Refill action column only when at least one row is an Rx —
  // otherwise we'd waste a column on legacy in-clinic medication records.
  const showRxColumn = medications.some((m: any) => m.isRx);

  // Show the delete column only when the parent passed totalActiveMedications.
  // The button is enabled only when there's more than one medication on the
  // horse so we never strip the last medication record entirely.
  const showDeleteColumn = typeof totalActiveMedications === 'number' && can.removeClinicalRecord;
  // ⚠ TWO INDEPENDENT REASONS TO REFUSE, ANDed, and they must stay separable. The
  // "at least one medication must remain" rule is CLINICAL and is explained in a tooltip on a
  // disabled button, because it is a state the person can change (delete a different one).
  // A missing permission is not: it has no cure on this screen, so the whole column is absent
  // rather than disabled with a tooltip nobody can act on. DELETE /medications/:id declares
  // manage:medical_records AND manage:medical_records_archive (medications.controller.ts:101).
  const canDelete = (totalActiveMedications ?? 0) > 1;

  const handleDispensed = () => {
    // Invalidate medications + dispense events so the row's "Refills X of Y"
    // chip updates without a manual refresh.
    queryClient.invalidateQueries('medications');
    queryClient.invalidateQueries(['dispense-events']);
    // Dispensing an Rx appends to (or creates) today's pending "RX-…" invoice
    // on the horse (backend appendDispenseToTodayInvoice). The chart's invoice
    // tab badge + header read ['horse-invoice-stats'] (fetched on mount) and
    // the invoice list reads ['horse-invoices']; without this the badge stays
    // stale (showed 0 while the panel listed the new invoice) until a reload.
    // Invalidate the prefixes so the current horse's queries refetch regardless
    // of horseId availability.
    queryClient.invalidateQueries(['horse-invoice-stats']);
    queryClient.invalidateQueries(['horse-invoices']);
  };

  /**
   * Reprint the label for the most recent fill of an Rx — for when a label
   * smudged, fell off, or got lost. This re-prints an EXISTING dispense
   * event's label; it does NOT record a new fill, touch inventory, or bill.
   * Uses the label size that fill was originally printed at, falling back to
   * the 30252 workhorse when none was recorded.
   */
  const handleReprint = async (med: any) => {
    setReprintingId(med.id);
    try {
      const events = await medicationService.listDispenseEvents(med.id);
      const latest = events.find((e) => !e.voidedAt);
      if (!latest) {
        enqueueSnackbar(
          'No fill on record to reprint — dispense this Rx first.',
          { variant: 'info' },
        );
        return;
      }
      // All labels print on the DYMO 30252 Address now; even if an older
      // fill was recorded at a different size, the reprint normalizes.
      const size = 'DYMO_30252' as const;
      const data = buildLabelData({
        size,
        medication: med as Medication,
        dispenseEvent: latest,
      });
      await printRxLabels({ size, labels: [{ data }] });
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message || 'Failed to reprint label',
        { variant: 'error' },
      );
    } finally {
      setReprintingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // archive() is a soft delete — preserves the row + audit trail, hides
      // it from the default medication list. Aligns with how the existing
      // Restore flow expects to find archived rows. Routed through executeUpdate
      // (flip isArchived) so it works offline too; executeUpdate NOT executeDelete
      // preserves the medication-history invariant.
      await executeUpdate({
        entityType: 'medication',
        table: 'medications',
        entityId: deleteTarget.id,
        payload: { isArchived: true, archivedAt: new Date().toISOString() },
        networkFn: () => medicationService.archive(deleteTarget.id),
      });
      queryClient.invalidateQueries('medications');
      if (horseId) queryClient.invalidateQueries(['medications', horseId]);
      queryClient.invalidateQueries(['horse-records', horseId]);
      enqueueSnackbar('Medication deleted', { variant: 'success' });
      setDeleteTarget(null);
    } catch (err: any) {
      if (err instanceof OfflineWriteBlockedError || err?.name === 'OfflineWriteBlockedError') {
        enqueueSnackbar(err.message, { variant: 'error' });
      } else {
        enqueueSnackbar(
          err?.response?.data?.message || 'Failed to delete medication',
          { variant: 'error' },
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {date && (
              <Typography
                variant="body2"
                color="textSecondary"
                data-testid="medication-card-date"
              >
                {formatCalendarDate(calendarProp(date), 'MMM dd, yyyy')}
              </Typography>
            )}
          </Box>
        </Box>

        {medications.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            {/* table-layout: fixed + explicit column widths so every
             * MedicationCard renders an identical grid. Without this each
             * card's table auto-sizes to its own content (e.g. a long
             * Instructions value), which left the Rx column landing at a
             * different x-position on every card. The Instructions column is
             * left width-less so it absorbs the remaining space and wraps. */}
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                minWidth: 720,
                '& td': { px: 1 },
                // Header text is clipped (not wrapped) so a narrow column's
                // label can never spill over the adjacent header.
                '& th': { px: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '22%' }}>Medication Name</TableCell>
                  <TableCell sx={{ width: 72 }}>Dosage</TableCell>
                  <TableCell sx={{ width: 64 }}>Quantity</TableCell>
                  <TableCell>Instructions</TableCell>
                  <TableCell sx={{ width: 88 }}>Start Date</TableCell>
                  {showRxColumn && <TableCell sx={{ width: 184 }}>Rx</TableCell>}
                  {showDeleteColumn && <TableCell align="right" sx={{ width: 108 }}> </TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {medications.map((med: any) => {
                  const isLowStock = lowStockItems.includes(med.name);
                  // ─── Model B Rx semantics ───────────────────────────────
                  // Display in "Fills X of Y" terms so vets see a counter
                  // that grows with each dispense (refills X of Y stays
                  // stuck after the initial fill, which confused users).
                  //   Y = refillsAuthorized + 1   (initial + refills)
                  //   X = dispenseEventCount      (non-voided fills)
                  // The Dispense button stays enabled until X reaches Y —
                  // refillsRemaining alone can't tell us if the initial
                  // fill has happened.
                  const refillsAuthorized = med.refillsAuthorized ?? 0;
                  const totalFillsAllowed = refillsAuthorized + 1;
                  const fillsDispensed = med._count?.dispenseEvents ?? 0;
                  // A COMPARISON, not a render - it mirrors the server's
                  // dispense gate so the button and the API agree on when an Rx
                  // stops dispensing. Day-to-day in the clinic's zone: an Rx
                  // expiring today is valid all day. See RxDispenseDialog.tsx.
                  const isExpired = isCalendarDayBeforeToday(med.expirationDate, clinicZone);
                  // ⚠ THE PERMISSION IS NOT ONE OF THESE TERMS, on purpose. Everything ANDed
                  // here is a CLINICAL reason ("no fills left", "expired", "C-II"), each shown
                  // as a tooltip on a disabled button because each is a fact about the Rx the
                  // person is entitled to know. A missing permission is not a fact about the
                  // Rx, so it removes the control instead of disabling it — see `showRxWrite`.
                  const canDispense = med.isRx && fillsDispensed < totalFillsAllowed && !isExpired
                    && med.deaSchedule !== 'II' && !med.isArchived;
                  // POST /medications/:id/dispense -> manage:medical_records (:110).
                  const showRxWrite = can.addClinicalRecord;
                  return (
                    <TableRow key={med.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          {med.name}
                          {isLowStock && (
                            <Chip
                              icon={<WarningIcon />}
                              label="Low Stock"
                              color="warning"
                              size="small"
                            />
                          )}
                          {med.deaSchedule && (
                            <Chip
                              label={`C-${med.deaSchedule}`}
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{med.dosage || '-'}</TableCell>
                      <TableCell>{med.quantity || '-'}</TableCell>
                      <TableCell>{med.instructions || '-'}</TableCell>
                      <TableCell data-testid="medication-start-date">
                        {/* `Medication.startDate` is a CALENDAR DAY on this path
                          * (DATE-KINDS.md §2.2) — a picked day from the medication
                          * dialog or barn ops — so it is rendered by its date PART.
                          * The column is MIXED at the COLUMN level (§1.2: SOAP
                          * auto-prescribe stamps `existingNote.date`, an instant,
                          * into it); collapsing that is a write-side fix, not a
                          * render one. */}
                        {formatCalendarDate(med.startDate, 'MM/dd/yy') || '-'}
                      </TableCell>
                      {showRxColumn && (
                        <TableCell>
                          {med.isRx ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {/* Clickable — opens fill history (dates, who
                               * dispensed, per-fill void). */}
                              <Tooltip title="View fill history">
                                <Chip
                                  data-testid="rx-fills-chip"
                                  label={`Fills ${fillsDispensed}/${totalFillsAllowed}`}
                                  size="small"
                                  color={canDispense ? 'success' : 'default'}
                                  onClick={() => setFillHistoryTarget(med as Medication)}
                                />
                              </Tooltip>
                              {isExpired && (
                                <Chip label="Expired" size="small" color="error" />
                              )}
                              {showRxWrite && (
                              <Tooltip
                                title={
                                  !canDispense
                                    ? isExpired
                                      ? 'Prescription expired'
                                      : fillsDispensed >= totalFillsAllowed
                                        ? 'All fills dispensed — a new Rx is required'
                                        : med.deaSchedule === 'II'
                                          ? 'Schedule II — not refillable'
                                          : 'Cannot dispense'
                                    : fillsDispensed === 0
                                      ? 'Dispense the initial fill of this Rx'
                                      : 'Dispense one refill of this Rx'
                                }
                              >
                                <span>
                                  <Button
                                    data-testid="rx-dispense-button"
                                    size="small"
                                    variant="contained"
                                    startIcon={<LocalPharmacyIcon />}
                                    disabled={!canDispense}
                                    onClick={() => setDispenseTarget(med as Medication)}
                                  >
                                    {fillsDispensed === 0 ? 'Dispense' : 'Refill'}
                                  </Button>
                                </span>
                              </Tooltip>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Not Rx
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {showDeleteColumn && (
                        <TableCell align="right">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                            {/* Reprint label — Rx rows only. Disabled until a
                             * fill exists (nothing to reprint before the
                             * initial dispense). */}
                            {med.isRx && (
                              <Tooltip
                                title={
                                  (med._count?.dispenseEvents ?? 0) === 0
                                    ? 'No fill on record yet — dispense to print a label'
                                    : 'Reprint the most recent label for this Rx'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={
                                      (med._count?.dispenseEvents ?? 0) === 0 ||
                                      reprintingId === med.id
                                    }
                                    onClick={() => handleReprint(med)}
                                    aria-label="Reprint label"
                                  >
                                    {reprintingId === med.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <PrintIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            {can.editClinicalRecord && (
                            <Tooltip title="Edit this medication">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setEditTarget(med as Medication)}
                                  aria-label="Edit medication"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            )}
                            <Tooltip
                              title={
                                canDelete
                                  ? 'Delete this medication'
                                  : 'Cannot delete — at least one medication must remain on the horse'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!canDelete}
                                  onClick={() => setDeleteTarget(med as Medication)}
                                  aria-label="Delete medication"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {dispenseTarget && (
          <RxDispenseDialog
            open={!!dispenseTarget}
            onClose={() => setDispenseTarget(null)}
            medication={dispenseTarget}
            onDispensed={handleDispensed}
          />
        )}

        {fillHistoryTarget && (
          <FillHistoryDialog
            open={!!fillHistoryTarget}
            onClose={() => setFillHistoryTarget(null)}
            medication={fillHistoryTarget}
            onChanged={handleDispensed}
          />
        )}

        {editTarget && (
          <EditMedicationDialog
            open={!!editTarget}
            onClose={() => setEditTarget(null)}
            medication={editTarget}
            horseId={horseId}
          />
        )}

        {/* Delete confirmation dialog */}
        <Dialog
          open={!!deleteTarget}
          onClose={() => !deleting && setDeleteTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete medication?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 1 }}>
              This will delete <strong>{deleteTarget?.name}</strong> from this horse's
              medication record. The record is archived rather than hard-deleted so
              an audit trail is preserved.
            </DialogContentText>
            {deleteTarget?.isRx && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This is a take-home Rx. Deleting will prevent further refills and
                remove it from the Rx history view.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={handleConfirmDelete}
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {medications.length === 0 && (
          <Typography variant="body2" color="textSecondary">
            No medications prescribed
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default MedicationCard;

